import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Separator } from './ui/separator';
import { Printer, Download, Save, Clock, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WorkCardDetailModal = ({ open, onClose, workcard, onUpdate }) => {
  const [taskDescription, setTaskDescription] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    if (workcard) {
      setTaskDescription(workcard.task_description);
      fetchCustomer();
    }
  }, [workcard]);

  const fetchCustomer = async () => {
    if (!workcard?.customer_id) return;
    try {
      const response = await axios.get(`${API}/customers/${workcard.customer_id}`);
      setCustomer(response.data);
    } catch (error) {
      console.error('Could not fetch customer:', error);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/workcards/${workcard.id}`, {
        task_description: taskDescription
      });
      toast.success('Arbejdskort opdateret');
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      toast.error('Kunne ikke gemme ændringer');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportTxt = async () => {
    try {
      const response = await axios.get(`${API}/workcards/${workcard.id}/export/txt`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `arbejdskort_${workcard.card_number}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('TXT fil downloadet');
    } catch (error) {
      toast.error('Kunne ikke eksportere');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatTime = (isoString) => {
    if (!isoString) return '--:--';
    const date = new Date(isoString);
    return date.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
  };

  if (!workcard) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="workcard-detail-modal">
        {/* Print Header - Hidden on screen */}
        <div className="hidden print:block">
          <style>{`
            @media print {
              body * { visibility: hidden; }
              .print-content, .print-content * { visibility: visible; }
              .print-content { position: absolute; left: 0; top: 0; width: 100%; }
            }
          `}</style>
        </div>

        <DialogHeader className="no-print">
          <DialogTitle className="flex items-center justify-between">
            <span>Arbejdskort #{workcard.card_number}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="icon" onClick={handleExportTxt} data-testid="detail-export-btn">
                <Download className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={handlePrint} data-testid="detail-print-btn">
                <Printer className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Printable Content */}
        <div className="print-content space-y-6 py-4">
          {/* Customer Info */}
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <h2 className="text-xl font-bold">{customer?.name || workcard.customer_name}</h2>
              <p className="text-muted-foreground">{customer?.phone || ''}</p>
              <p className="text-muted-foreground">{customer?.email || ''}</p>
              <p className="text-muted-foreground">{customer?.address || ''}</p>
              <p className="font-mono text-sm mt-2">nr: {workcard.card_number}</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-primary">ARBEJDSKORT</p>
              <p className="text-sm text-muted-foreground">
                Oprettet: {new Date(workcard.created_at).toLocaleDateString('da-DK')}
              </p>
            </div>
          </div>

          <Separator />

          {/* Task Description */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Arbejdsopgave</h3>
              {!isEditing && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  className="no-print"
                  data-testid="edit-task-btn"
                >
                  Rediger
                </Button>
              )}
            </div>
            
            {isEditing ? (
              <div className="space-y-2 no-print">
                <Textarea
                  value={taskDescription}
                  onChange={(e) => setTaskDescription(e.target.value)}
                  rows={5}
                  data-testid="edit-task-textarea"
                />
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsEditing(false);
                      setTaskDescription(workcard.task_description);
                    }}
                  >
                    Annuller
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={isSaving}
                    data-testid="save-task-btn"
                  >
                    {isSaving ? (
                      <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-1" />
                        Gem
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="whitespace-pre-wrap text-foreground bg-muted/30 p-4 rounded-lg min-h-[100px]">
                {workcard.task_description || 'Ingen beskrivelse'}
              </div>
            )}
          </div>

          <Separator />

          {/* Time Logs */}
          <div className="space-y-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Tidsregistrering
            </h3>
            
            {/* Active Clock-ins */}
            {workcard.active_clock_ins?.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-green-600 dark:text-green-400">Aktive stempler:</p>
                {workcard.active_clock_ins.map((clock, idx) => (
                  <div key={idx} className="text-sm p-2 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                    <span className="font-medium">{clock.username}</span>
                    {' '}- Stemplet ind kl. {formatTime(clock.clock_in)}
                  </div>
                ))}
              </div>
            )}

            {/* Completed Time Logs */}
            {workcard.time_logs?.length > 0 ? (
              <div className="space-y-1 font-mono text-sm">
                {workcard.time_logs.map((log, idx) => (
                  <p key={idx} className="text-muted-foreground">
                    {log.username} ({formatTime(log.clock_in)} - {formatTime(log.clock_out)}, Arbejdstid {log.duration_minutes} min) {log.date}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Ingen tidsregistreringer endnu</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WorkCardDetailModal;
